import React, { useState, useLayoutEffect, useEffect } from "react";
import "./App.css";
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import PremiumBackground from "./components/PremiumBackground";
import GlobalEffects from "./components/GlobalEffects";
import PironeAIChat, { PironeAIButton } from "./components/PironeAIChat";
import LiveNotification from "./components/LiveNotification";
import ExitIntentPopup from "./components/ExitIntentPopup";
import ScrollToTopButton from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import SocialMediaManagementPage from "./pages/SocialMediaManagementPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import MorristownVisibilityPage from "./pages/MorristownVisibilityPage";
import AdminPage from "./pages/AdminPage";
import WebDesignPage from "./pages/WebDesignPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import OurWorkPage from "./pages/OurWorkPage";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track visitor on page load
const VisitorTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        await fetch(`${API_URL}/api/track-visitor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            userAgent: navigator.userAgent
          })
        });
      } catch (err) {
        // Visitor tracking is non-critical - log for debugging but don't crash the app
        console.debug('Visitor tracking failed:', err?.message);
      }
    };
    
    trackVisitor();
  }, [location.pathname]);
  
  return null;
};

// Scroll to top on route change - instant, no animation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    // Instant scroll - no smooth behavior
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

// Layout wrapper that conditionally renders header/footer
const Layout = ({ children }) => {
  
  return (
    <>
      <ScrollToTop />
      <Header />
      {children}
      <Footer />
    </>
  );
};

function App() {
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isAIChatMinimized, setIsAIChatMinimized] = useState(false);

  const handleOpenChat = () => {
    setIsAIChatOpen(true);
    setIsAIChatMinimized(false);
  };

  const handleCloseChat = () => {
    setIsAIChatOpen(false);
    setIsAIChatMinimized(false);
  };

  const handleMinimizeChat = () => {
    setIsAIChatMinimized(!isAIChatMinimized);
  };

  return (
    <LanguageProvider>
      <div className="App bg-black min-h-screen overflow-x-hidden">
        <PremiumBackground />
        <BrowserRouter>
          {/* Global Effects - Mouse Trail & Scroll Progress */}
          <GlobalEffects />
          
          {/* Visitor Tracking */}
          <VisitorTracker />
          
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/social-media-management" element={<SocialMediaManagementPage />} />
              <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/new-jersey" element={<MorristownVisibilityPage />} />
              <Route path="/web-design" element={<WebDesignPage />} />
              <Route path="/our-work" element={<OurWorkPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </Layout>
          
          {/* PIRONE AI + WhatsApp - Combined split floating button (bottom-right) */}
          {!isAIChatOpen && <PironeAIButton onClick={handleOpenChat} />}
          <PironeAIChat 
            isOpen={isAIChatOpen} 
            onClose={handleCloseChat}
            onMinimize={handleMinimizeChat}
            isMinimized={isAIChatMinimized}
          />
          {!isAIChatOpen && <LiveNotification onOpenChat={handleOpenChat} delay={30000} />}
          
          {/* Exit Intent Popup */}
          <ExitIntentPopup />
          
          {/* Scroll to Top Button */}
          <ScrollToTopButton />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
